import { SetStateAction, useMemo, useState } from 'react';

import { Activity, ActivityType, TreeActivity } from '@/features/course/admin/types/activity';
import { useQueryBooleanState } from '@/hooks/useQueryBooleanState';
import { convertActivitiesToSortedNavigationList, convertActivitiesToTree } from '@/pages/ModularLearning/utils';
import { ActivityId } from '@/administration/pages/Course/store/types';
import { getAreAllActivitiesCompleted } from '@/administration/pages/Course/store/activity/getAreAllActivitiesCompleted';

type UseModularLearningViewerResult = {
  areAllActivitiesCompleted: boolean;
  activities: Activity[];
  isDrawerOpen: boolean;
  treeActivities: TreeActivity[];
  activityActiveCount: number;
  previousActiveActivityIndex: number;
  activitiesTotalCount: number;
  onSetActivityActive: (ops: { activityId: ActivityId }) => void;
  onSetPrevActivityActive: () => void;
  onSetNextActivityActive: () => void;
  onSetActivityActiveByIndex: (index: number) => void;
  setDrawerOpen: (value: boolean) => void;
  setActivities: (value: SetStateAction<Activity[]>) => void;
};

export const useModularLearningViewerV2 = (): UseModularLearningViewerResult => {
  const [activities, setActivities] = useState<Activity[]>([]);
  const [isDrawerOpen, setDrawerOpen] = useQueryBooleanState('drawer-open', true);
  const [previousActiveActivityIndex, setPreviousActiveActivityIndex] = useState(0);

  const treeActivities = useMemo(() => {
    return convertActivitiesToTree(activities);
  }, [activities]);

  // Activities flat list (sorted & without modules)
  const sortedNavigationList = useMemo(() => convertActivitiesToSortedNavigationList(activities), [activities]);
  const activityActiveCount = sortedNavigationList?.findIndex((activity) => activity.active);
  const activitiesTotalCount = sortedNavigationList?.length;

  const handleActivateActivity = ({ activityId }: { activityId: ActivityId }) => {
    const activeActivityIndex = sortedNavigationList.findIndex((activity) => activity.active);

    setPreviousActiveActivityIndex(activeActivityIndex);

    setActivities((oldActivities: Activity[]) =>
      oldActivities.map((activity: Activity) =>
        activity.id === activityId ? { ...activity, active: true } : { ...activity, active: false },
      ),
    );
  };

  const handleSetPrevActivityActive = () => {
    let indexOf = sortedNavigationList.findIndex((activity) => activity.active) - 1;
    if (indexOf < 0) indexOf = 0;

    handleActivateActivity({ activityId: sortedNavigationList[indexOf].id });
  };

  const handleSetNextActivityActive = () => {
    let indexOfActiveActivity = sortedNavigationList.findIndex((activity) => activity.active) + 1;
    if (indexOfActiveActivity === sortedNavigationList.length) {
      const firstIncompleteActivity = sortedNavigationList.find(
        (activity) => !activity.completed && activity.type !== ActivityType.Module,
      );

      if (firstIncompleteActivity) {
        handleActivateActivity({ activityId: firstIncompleteActivity.id });
      }
    } else {
      if (indexOfActiveActivity >= sortedNavigationList.length) indexOfActiveActivity = sortedNavigationList.length - 1;

      handleActivateActivity({ activityId: sortedNavigationList[indexOfActiveActivity].id });
    }
  };

  const handleSetActivityActiveByIndex = (index: number) => {
    const parsedIndex = index >= activities.length ? activities.length - 1 : index;

    handleActivateActivity({ activityId: activities[parsedIndex].id });
  };

  const areAllActivitiesCompleted = useMemo(() => {
    return getAreAllActivitiesCompleted(activities);
  }, [activities]);

  return {
    areAllActivitiesCompleted,
    activities,
    isDrawerOpen,
    treeActivities,
    activityActiveCount,
    previousActiveActivityIndex,
    activitiesTotalCount,
    onSetActivityActive: handleActivateActivity,
    onSetPrevActivityActive: handleSetPrevActivityActive,
    onSetNextActivityActive: handleSetNextActivityActive,
    onSetActivityActiveByIndex: handleSetActivityActiveByIndex,
    setDrawerOpen,
    setActivities,
  };
};
